// Which airspace a route enters, and who to call there: the in-flight half
// of the pilot log sheet. Pure and shared so the browser answers from the
// static airspace.geojson with no signal, using the callsigns and
// frequencies the eAIP parse already put on each feature.
//
// Routes are sampled every STEP_NM and each sample tested against the
// polygons. A corner clipped by less than that between two samples is
// missed; the sheet is a briefing aid and says so, the map is the check.

import { distanceNm } from './navigation'
import type { AirspaceProperties, AirspaceType, Position } from './airspace'

export interface AirspaceShape {
  properties: AirspaceProperties
  geometry:
    | { type: 'Polygon'; coordinates: Position[][] }
    | { type: 'MultiPolygon'; coordinates: Position[][][] }
}

export interface AirspaceOnRoute {
  airspace: AirspaceProperties
  /** Nautical miles from the departure at which the route first enters it. */
  enteredNm: number
}

/** Airspace a pilot needs a call or a clearance for, or must stay out of. */
export const CONTROLLED_ROW_TYPES: readonly AirspaceType[] = ['CTR', 'TMA', 'CTA', 'RMZ', 'P', 'R', 'D']
/** Where the en-route frequency is the FIR or flight information zone's. */
export const ENROUTE_ROW_TYPES: readonly AirspaceType[] = ['FIR', 'FIZ']

const STEP_NM = 0.5

interface Sample {
  lat: number
  lon: number
  nm: number
}

function sampleRoute(points: { lat: number; lon: number }[]): Sample[] {
  const samples: Sample[] = []
  let flown = 0
  points.forEach((point, index) => {
    const previous = points[index - 1]
    if (!previous) {
      samples.push({ ...point, nm: 0 })
      return
    }
    const length = distanceNm(previous.lat, previous.lon, point.lat, point.lon)
    const steps = Math.max(1, Math.ceil(length / STEP_NM))
    for (let step = 1; step <= steps; step++) {
      const f = step / steps
      samples.push({
        lat: previous.lat + (point.lat - previous.lat) * f,
        lon: previous.lon + (point.lon - previous.lon) * f,
        nm: flown + length * f,
      })
    }
    flown += length
  })
  return samples
}

function inRing(lon: number, lat: number, ring: Position[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!
    const [xj, yj] = ring[j]!
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

// The first ring is the boundary, any others are holes.
function inPolygon(lon: number, lat: number, rings: Position[][]): boolean {
  const [outer, ...holes] = rings
  return !!outer && inRing(lon, lat, outer) && !holes.some((hole) => inRing(lon, lat, hole))
}

function polygonsOf(shape: AirspaceShape): Position[][][] {
  return shape.geometry.type === 'Polygon' ? [shape.geometry.coordinates] : shape.geometry.coordinates
}

/**
 * The airspace of the given types that the route through `points` enters,
 * in the order it is entered. Each airspace appears once.
 */
export function airspaceOnRoute(
  points: { lat: number; lon: number }[],
  shapes: AirspaceShape[],
  types: readonly AirspaceType[],
): AirspaceOnRoute[] {
  if (points.length === 0) return []
  const samples = sampleRoute(points)
  const found: AirspaceOnRoute[] = []
  for (const shape of shapes) {
    if (!types.includes(shape.properties.type)) continue
    const polygons = polygonsOf(shape)
    const hit = samples.find((s) => polygons.some((rings) => inPolygon(s.lon, s.lat, rings)))
    if (hit) found.push({ airspace: shape.properties, enteredNm: hit.nm })
  }
  return found.sort((a, b) => a.enteredNm - b.enteredNm || a.airspace.id.localeCompare(b.airspace.id))
}

export interface RouteFrequencyRow {
  /** The airspace's designator; where several parts share one unit, the first. */
  id: string
  type: AirspaceType
  callsign: string | null
  freq: string | null
  lower: string
  upper: string
  activity: string | null
}

/**
 * One row per unit to call. A TMA is published in layers (EFHKL, EFHKU)
 * that share a callsign and frequency: they collapse into the first. Areas
 * with no frequency (restricted, danger) stay one row each.
 */
export function routeFrequencyRows(found: AirspaceOnRoute[]): RouteFrequencyRow[] {
  const rows = new Map<string, RouteFrequencyRow>()
  for (const { airspace: a } of found) {
    const key = a.freq ? `${a.type}|${a.callsign}|${a.freq}` : a.id
    if (rows.has(key)) continue
    rows.set(key, {
      id: a.id,
      type: a.type,
      callsign: a.callsign,
      freq: a.freq,
      lower: a.lowerText,
      upper: a.upperText,
      activity: a.activity,
    })
  }
  return [...rows.values()]
}
